import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import fetchApi from '../utils/api';
import Swal from 'sweetalert2';
import AirDatePicker from '../components/ui/AirDatePicker';

const today = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatToDDMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
        if (parts[0].length === 4) {
            // YYYY-MM-DD to DD-MM-YYYY
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return dateStr; // Already DD-MM-YYYY
    }
    return dateStr;
};

const getDayName = (dateStr) => {
    if (!dateStr) return '';
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return days[date.getDay()];
    } catch {
        return '';
    }
};

function AddSalesReturnForm({ onClose, onSaved }) {
    const [customers, setCustomers] = useState([]);
    const [invoiceNames, setInvoiceNames] = useState([]);
    const [allInvoices, setAllInvoices] = useState([]);

    // Header fields
    const [paymentType, setPaymentType] = useState('Debit');
    const [invoiceTypeName, setInvoiceTypeName] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerDropdown, setCustomerDropdown] = useState(false);
    const [customerActiveIdx, setCustomerActiveIdx] = useState(-1);
    const customerDropRef = useRef(null);
    const paymentTypeRef = useRef(null);
    const partySearchRef = useRef(null);
    const invoiceTypeRef = useRef(null);
    const saleNoRef = useRef(null);
    const returnNoRef = useRef(null);
    const discountRef = useRef(null);
    const insuranceRef = useRef(null);
    const extraChargesRef = useRef(null);
    const qtyRefs = useRef([]);

    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [saleDate, setSaleDate] = useState('');
    const [returnNo, setReturnNo] = useState('');
    const [returnDate, setReturnDate] = useState(today());
    const [currentTime, setCurrentTime] = useState('');

    // Items
    const [items, setItems] = useState([]);

    // Footer
    const [discountPct, setDiscountPct] = useState('0');
    const [insurancePct, setInsurancePct] = useState(0);
    const [extraChargesInput, setExtraChargesInput] = useState(0);

    const [note, setNote] = useState('100% PAYMENT AGAINST SCAN COPY OF DOCUMENTS.');
    const [narration, setNarration] = useState(
        '1. Goods Once Supplied will not be taken back or exchanged.\n' +
        '2. We are not responsible for any shortage or damage in transist.\n' +
        '3. Interest at the rate of 18% shall be charged on all amounts unpaid within 15 days from the supply date.'
    );
    const [changeDeliveryAddress, setChangeDeliveryAddress] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Live clock for Time field
    useEffect(() => {
        const updateClock = () => {
            const now = new Date();
            let hours = now.getHours();
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            const ampm = hours >= 12 ? 'pm' : 'am';
            hours = hours % 12;
            hours = hours ? hours : 12;
            setCurrentTime(`${hours}:${minutes}:${seconds} ${ampm}`);
        };
        updateClock();
        const timer = setInterval(updateClock, 1000);
        return () => clearInterval(timer);
    }, []);

    // Load customers and invoice names on mount
    useEffect(() => {
        const load = async () => {
            try {
                const res = await fetchApi('/customer?limit=1000');
                setCustomers(res.customers || (Array.isArray(res) ? res : []));
            } catch (e) { console.error(e); }
            try {
                const res = await fetchApi('/invoice-name');
                const loadedNames = Array.isArray(res) ? res : (res.invoiceNames || res.data || []);
                setInvoiceNames(loadedNames);
                if (loadedNames.length > 0) {
                    setInvoiceTypeName(loadedNames[0].invoice_short_name || loadedNames[0].invoiceShortName);
                } else {
                    setInvoiceTypeName('Tax invoice');
                }
            } catch (e) { console.error(e); }
        };
        load();
    }, []);

    // When customer is selected, load their invoices
    useEffect(() => {
        if (!selectedCustomer) { setAllInvoices([]); setSelectedInvoice(null); return; }
        const load = async () => {
            try {
                const res = await fetchApi(`/sales-return/invoices-by-customer?customerId=${selectedCustomer.id}`);
                setAllInvoices(res.invoices || []);
            } catch (e) { console.error(e); }
        };
        load();
    }, [selectedCustomer]);

    // When invoice is selected, load its products, insurance, extra charges and return number
    useEffect(() => {
        if (!selectedInvoice) { 
            setItems([]); 
            setInsurancePct(0); 
            setDiscountPct('0');
            setExtraChargesInput(0); 
            setSaleDate('');
            return; 
        }
        setSaleDate(selectedInvoice.invoiceDate || '');
        
        // Populate discount, insurance and extra charges from invoice
        const invoiceInsurance = parseFloat(selectedInvoice.insurance) || 0;
        const invoiceExtraCharges = parseFloat(selectedInvoice.extraChargesAmount) || 0;
        const invoiceDiscount = selectedInvoice.discount !== undefined ? String(selectedInvoice.discount) : '0';
        
        setInsurancePct(invoiceInsurance);
        setDiscountPct(invoiceDiscount);
        setExtraChargesInput(invoiceExtraCharges);

        // Map items exactly match invoice structure
        const rawItems = selectedInvoice.items || [];
        setItems(rawItems.map(it => ({
            category: it.category || '',
            product: it.product || it.productName || it.name || '',
            hsnCode: it.hsnCode || it.hsn_code || '',
            grade: it.grade || '',
            color: it.color || '',
            unit: it.unit || 'PCS',
            qty: parseFloat(it.quantity || it.qty || 0),
            rate: parseFloat(it.rate || 0),
            amount: parseFloat(it.total || it.amount || 0),
        })));

    }, [selectedInvoice]);

    // Fetch next return no whenever invoice type changes
    useEffect(() => {
        if (invoiceTypeName) {
            fetchApi(`/sales-return/next-no?typeName=${encodeURIComponent(invoiceTypeName)}`)
                .then(r => setReturnNo(r.nextNo || ''))
                .catch(() => {});
        }
    }, [invoiceTypeName]);

    // Customer search filter
    const filteredCustomers = (() => {
        const q = customerSearch.toLowerCase();
        if (!q) return customers.slice(0, 15);
        return customers.filter(c => {
            const name = (c.customer_trade_name || c.customer_name || '').toLowerCase();
            return name.includes(q);
        }).slice(0, 15);
    })();

    const selectCustomer = (c) => {
        setSelectedCustomer(c);
        setCustomerSearch(c.customer_trade_name || c.customer_name);
        setCustomerDropdown(false);
        setSelectedInvoice(null);
        setItems([]);
    };

    // Item qty change
    const updateItemQty = (idx, val) => {
        setItems(prev => prev.map((it, i) => {
            if (i !== idx) return it;
            const qty = parseFloat(val) || 0;
            return { ...it, qty, amount: parseFloat((qty * it.rate).toFixed(2)) };
        }));
    };

    const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

    // Totals calculations
    const subTotal = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
    const discountAmt = parseFloat(((subTotal * (parseFloat(discountPct) || 0)) / 100).toFixed(2));
    const insAmt = parseFloat(((subTotal * (parseFloat(insurancePct) || 0)) / 100).toFixed(2));
    const extrAmt = parseFloat(extraChargesInput) || 0;

    const taxableAmount = parseFloat((subTotal - discountAmt + insAmt + extrAmt).toFixed(2));

    // GST calculation
    const tax1 = parseFloat(selectedInvoice?.saleTypeTax1 || 9);
    const tax2 = parseFloat(selectedInvoice?.saleTypeTax2 || 9);
    const centralTax = parseFloat(((taxableAmount * tax1) / 100).toFixed(2));
    const stateTax = parseFloat(((taxableAmount * tax2) / 100).toFixed(2));
    const billAmount = parseFloat((taxableAmount + centralTax + stateTax).toFixed(2));

    const handleSubmit = async () => {

    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;
        if (!selectedCustomer) { toast.error('Please select a customer'); return; }
        if (!returnNo) { toast.error('Return number is required'); return; }
        if (items.length === 0) { toast.error('At least one item is required'); return; }
        setSubmitting(true);
        try {
            await fetchApi('/sales-return', {
                method: 'POST',
                body: JSON.stringify({
                    originalInvoiceId: selectedInvoice?.id || null,
                    originalInvoiceNo: selectedInvoice?.invoiceNo || null,
                    returnNo,
                    returnDate,
                    invoiceTypeName: invoiceTypeName,
                    invoiceShortName: selectedInvoice?.invoiceShortName || selectedInvoice?.invoiceTypeName || invoiceTypeName,
                    paymentType,
                    customerId: selectedCustomer?.id,
                    customerName: selectedCustomer?.customer_name,
                    customerTradeName: selectedCustomer?.customer_trade_name,
                    customerGst: selectedCustomer?.customer_gst,
                    customerAddress: selectedCustomer?.customer_address,
                    customerState: selectedCustomer?.customer_state,
                    customerStateCode: selectedCustomer?.customer_state_code,
                    saleDate,
                    items,
                    discount: parseFloat(discountPct) || 0,
                    discountType: '%',
                    courierCharges: 0,
                    packingForwarding: 0,
                    insurance: insAmt,
                    extraChargesAmount: extrAmt,
                    saleTypeTax1: tax1,
                    saleTypeTax2: tax2,
                    subTotal,
                    taxableAmount,
                    centralTax,
                    stateTax,
                    totalAmount: billAmount,
                    note,
                    narration,
                })
            });
            toast.success('Sales return saved successfully!');
            if (onSaved) onSaved();
            if (onClose) onClose();
        } catch (err) {
            toast.error(err.message || 'Failed to save sales return');
        } finally {
            setSubmitting(false);
        }
    };

    // Auto-focus on mount
    useEffect(() => {
        if (paymentTypeRef.current) {
            paymentTypeRef.current.focus();
        }
    }, []);

    const handlePaymentTypeKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (partySearchRef.current) {
                partySearchRef.current.focus();
            }
        }
    };

    const handlePartySearchKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setCustomerDropdown(true);
            setCustomerActiveIdx(prev => {
                const next = prev + 1;
                return next < filteredCustomers.length ? next : prev;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setCustomerActiveIdx(prev => {
                const next = prev - 1;
                return next >= 0 ? next : 0;
            });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (customerDropdown && customerActiveIdx >= 0 && filteredCustomers[customerActiveIdx]) {
                selectCustomer(filteredCustomers[customerActiveIdx]);
                setTimeout(() => {
                    if (invoiceTypeRef.current) invoiceTypeRef.current.focus();
                }, 100);
            } else if (selectedCustomer) {
                if (invoiceTypeRef.current) invoiceTypeRef.current.focus();
            } else if (filteredCustomers.length > 0) {
                selectCustomer(filteredCustomers[0]);
                setTimeout(() => {
                    if (invoiceTypeRef.current) invoiceTypeRef.current.focus();
                }, 100);
            }
        }
    };

    const handleInvoiceTypeKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (saleNoRef.current) {
                saleNoRef.current.focus();
            }
        }
    };

    const handleSaleNoKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (returnNoRef.current) {
                returnNoRef.current.focus();
            }
        }
    };

    const handleReturnNoKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (qtyRefs.current[0]) {
                qtyRefs.current[0].focus();
                qtyRefs.current[0].select();
            } else if (discountRef.current) {
                discountRef.current.focus();
                discountRef.current.select();
            }
        }
    };

    const handleQtyKeyDown = (idx, e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (qtyRefs.current[idx + 1]) {
                qtyRefs.current[idx + 1].focus();
                qtyRefs.current[idx + 1].select();
            } else if (discountRef.current) {
                discountRef.current.focus();
                discountRef.current.select();
            }
        }
    };

    const handleDiscountKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (insuranceRef.current) {
                insuranceRef.current.focus();
                insuranceRef.current.select();
            }
        }
    };

    const handleInsuranceKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (extraChargesRef.current) {
                extraChargesRef.current.focus();
                extraChargesRef.current.select();
            }
        }
    };

    // Shared Miracle style styles
    const thStyle = {
        backgroundColor: '#1e3a5f',
        color: '#ffffff',
        fontSize: '12px',
        fontWeight: '500',
        padding: '6px 8px',
        borderRight: '1px solid #365072',
        borderBottom: '1px solid #c2c2c2',
        textAlign: 'left'
    };

    const tdStyle = {
        padding: '2px 4px',
        borderRight: '1px solid #e1e5e8',
        borderBottom: '1px solid #e1e5e8',
        background: '#ffffff'
    };

    const cellInputStyle = {
        width: '100%',
        border: 'none',
        background: 'transparent',
        fontSize: '13px',
        height: '24px',
        outline: 'none',
        color: '#000000',
        padding: '0 4px',
        boxSizing: 'border-box'
    };

    return (
        <div className="add-sales-return-form" style={{ background: '#f8fafc', borderRadius: '12px', padding: '28px', maxWidth: '1250px', margin: '20px auto', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', fontFamily: 'Arial, sans-serif' }}>
            
            {/* Header Title */}
            <div style={{ borderBottom: '2px solid #e2e8f0', paddingBottom: '16px', marginBottom: '24px' }}>
                <h2 style={{ textAlign: 'center', fontSize: '26px', fontWeight: '800', color: '#0f172a', margin: 0, letterSpacing: '-0.5px' }}>Add Sale Return</h2>
            </div>

            {/* ── 3-Column Top Header Section ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '24px', marginBottom: '20px', background: '#ffffff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                
                {/* Column 1: Cash/Debit & Party A/c */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div>
                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '6px' }}>Cash/Debit</label>
                        <select
                            ref={paymentTypeRef}
                            value={paymentType}
                            onChange={e => setPaymentType(e.target.value)}
                            onKeyDown={handlePaymentTypeKeyDown}
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#fff', color: '#1e293b', fontWeight: '500' }}
                        >
                            <option value="Debit">Debit</option>
                            <option value="Credit">Credit</option>
                            <option value="Cash">Cash</option>
                        </select>
                    </div>

                    <div>
                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '6px' }}>Party A/c.</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <div style={{ position: 'relative', flex: 1 }}>
                                <input
                                    ref={partySearchRef}
                                    placeholder="Search customer..."
                                    value={customerSearch}
                                    onChange={e => {
                                        setCustomerSearch(e.target.value);
                                        setCustomerDropdown(true);
                                        setCustomerActiveIdx(-1);
                                        if (!e.target.value) { setSelectedCustomer(null); setAllInvoices([]); setSelectedInvoice(null); }
                                    }}
                                    onFocus={() => setCustomerDropdown(true)}
                                    onBlur={() => setTimeout(() => setCustomerDropdown(false), 200)}
                                    onKeyDown={handlePartySearchKeyDown}
                                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                                />
                                {customerDropdown && filteredCustomers.length > 0 && (
                                    <div ref={customerDropRef} style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 10px 20px rgba(0,0,0,0.1)', zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                                        {filteredCustomers.map((c, pi) => (
                                            <div
                                                key={c.id}
                                                onMouseDown={() => selectCustomer(c)}
                                                onMouseEnter={() => setCustomerActiveIdx(pi)}
                                                style={{ padding: '10px 14px', cursor: 'pointer', background: customerActiveIdx === pi ? '#f1f5f9' : '#fff', borderBottom: '1px solid #f1f5f9' }}
                                            >
                                                <div style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>{c.customer_trade_name || c.customer_name}</div>
                                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                                    {c.customer_gst && `GST: ${c.customer_gst} || `}{c.customer_state} || Bal: {Math.abs(parseFloat(c.balance || 0)).toFixed(2)} {parseFloat(c.balance || 0) >= 0 ? 'DR' : 'CR'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        {selectedCustomer && (
                            <div style={{ marginTop: '8px', paddingLeft: '4px' }}>
                                <div style={{ fontSize: '13px', color: '#2563eb', fontWeight: '700' }}>{selectedCustomer.customer_state}</div>
                                <div style={{ fontSize: '13px', color: '#0f172a', fontWeight: '800', marginTop: '2px' }}>Bal. : {Math.abs(parseFloat(selectedCustomer.balance || 0)).toFixed(2)} {parseFloat(selectedCustomer.balance || 0) >= 0 ? 'DR' : 'CR'}</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Column 2: Invoice Type & Time */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div>
                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '6px' }}>Invoice Type</label>
                        <select
                            ref={invoiceTypeRef}
                            value={invoiceTypeName}
                            onChange={e => setInvoiceTypeName(e.target.value)}
                            onKeyDown={handleInvoiceTypeKeyDown}
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#fff', color: '#1e293b', fontWeight: '500' }}
                        >
                            {invoiceNames.length === 0 && <option value="Tax invoice">Tax invoice</option>}
                            {invoiceNames.map(n => (
                                <option key={n.id} value={n.invoice_short_name || n.invoiceShortName}>{n.invoice_short_name || n.invoiceShortName}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '6px' }}>Time</label>
                        <input value={currentTime} readOnly style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: '#f8fafc', color: '#64748b' }} />
                    </div>
                </div>

                {/* Column 3: Sale No, Sale Date, Return No, Return Date */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    
                    {/* Sale No */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#475569', width: '90px' }}>Sale No</span>
                        <select
                            ref={saleNoRef}
                            value={selectedInvoice?.id || ''}
                            onChange={e => {
                                const inv = allInvoices.find(i => String(i.id) === String(e.target.value));
                                setSelectedInvoice(inv || null);
                            }}
                            onKeyDown={handleSaleNoKeyDown}
                            style={{ flex: 1, padding: '7px 10px', border: '1px solid #86efac', borderRadius: '6px', fontSize: '14px', background: '#f0fdf4', color: '#166534', fontWeight: '600' }}
                            disabled={!selectedCustomer}
                        >
                            <option value="">Select Sale No</option>
                            {allInvoices.map(inv => (
                                <option key={inv.id} value={inv.id}>{inv.invoiceNo}</option>
                            ))}
                        </select>
                    </div>

                    {/* Sale Date */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#475569', width: '90px' }}>Sale Date</span>
                        <div style={{ flex: 1, display: 'flex', gap: '6px' }}>
                            <input
                                value={formatToDDMMYYYY(saleDate)}
                                readOnly
                                style={{ flex: 1, padding: '7px 10px', border: '1px solid #86efac', borderRadius: '6px', fontSize: '14px', background: '#f0fdf4', color: '#166534', fontWeight: '600' }}
                            />
                            {saleDate && (
                                <span style={{ padding: '6px 10px', background: '#bbf7d0', color: '#166534', borderRadius: '6px', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center' }}>
                                    {getDayName(saleDate)}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Return No */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#475569', width: '90px' }}>Return No</span>
                        <input
                            ref={returnNoRef}
                            value={returnNo}
                            onChange={e => setReturnNo(e.target.value)}
                            onKeyDown={handleReturnNoKeyDown}
                            placeholder="Auto-generated"
                            style={{ flex: 1, padding: '7px 10px', border: '1px solid #86efac', borderRadius: '6px', fontSize: '14px', background: '#f0fdf4', color: '#166534', fontWeight: '600' }}
                        />
                    </div>

                    {/* Return Date */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#475569', width: '90px' }}>Return Date</span>
                        <div style={{ flex: 1, display: 'flex', gap: '6px' }}>
                            <div style={{ flex: 1 }}>
                                <AirDatePicker
                                    value={returnDate}
                                    onChange={(dateStr) => setReturnDate(dateStr)}
                                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #86efac', borderRadius: '6px', fontSize: '14px', background: '#f0fdf4', color: '#166534', fontWeight: '600', boxSizing: 'border-box' }}
                                />
                            </div>
                            <span style={{ padding: '6px 10px', background: '#bbf7d0', color: '#166534', borderRadius: '6px', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center' }}>
                                {getDayName(returnDate)}
                            </span>
                        </div>
                    </div>

                </div>
            </div>

            {/* ── Product Table (Exactly styled to match Miracle Billing design in screenshot) ── */}
            <div className="miracle-table-wrapper" style={{ marginTop: '15px', border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden', background: '#ffffff', marginBottom: '24px' }}>
                <table className="miracle-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '950px' }}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Category *</th>
                            <th style={{ ...thStyle, minWidth: '220px' }}>Product Name *</th>
                            <th style={thStyle}>HSN Code</th>
                            <th style={{ ...thStyle, width: '100px' }}>Grade</th>
                            <th style={{ ...thStyle, width: '100px' }}>Color</th>
                            <th style={{ ...thStyle, width: '80px' }}>Unit</th>
                            <th style={{ ...thStyle, width: '80px', textAlign: 'center' }}>Qty</th>
                            <th style={{ ...thStyle, width: '100px' }}>Rate *</th>
                            <th style={{ ...thStyle, width: '110px', textAlign: 'right', borderRight: 'none' }}>Amount</th>
                            <th style={{ ...thStyle, width: '40px', background: '#1e3a5f', borderRight: 'none' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr>
                                <td colSpan={10} style={{ textAlign: 'center', color: '#94a3b8', padding: '30px', fontSize: '13px', borderBottom: 'none' }}>
                                    {selectedInvoice ? 'No items in the selected invoice' : 'Select a sale invoice to load products'}
                                </td>
                            </tr>
                        ) : (
                            items.map((it, idx) => (
                                <tr key={idx}>
                                    {/* Category - READONLY */}
                                    <td style={tdStyle}>
                                        <input value={it.category} readOnly style={{ ...cellInputStyle, color: '#666666', cursor: 'not-allowed' }} />
                                    </td>
                                    {/* Product Name - READONLY */}
                                    <td style={tdStyle}>
                                        <input value={it.product} readOnly style={{ ...cellInputStyle, color: '#666666', cursor: 'not-allowed' }} />
                                    </td>
                                    {/* HSN Code - READONLY */}
                                    <td style={tdStyle}>
                                        <input value={it.hsnCode} readOnly style={{ ...cellInputStyle, color: '#666666', cursor: 'not-allowed' }} />
                                    </td>
                                    {/* Grade */}
                                    <td style={tdStyle}>
                                        <input value={it.grade} readOnly style={cellInputStyle} />
                                    </td>
                                    {/* Color */}
                                    <td style={tdStyle}>
                                        <input value={it.color} readOnly style={cellInputStyle} />
                                    </td>
                                    {/* Unit */}
                                    <td style={tdStyle}>
                                        <input value={it.unit} readOnly style={cellInputStyle} />
                                    </td>
                                    {/* Qty - EDITABLE */}
                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                        <input
                                            ref={el => qtyRefs.current[idx] = el}
                                            type="number"
                                            value={it.qty}
                                            min={0}
                                            onChange={e => updateItemQty(idx, e.target.value)}
                                            onKeyDown={e => handleQtyKeyDown(idx, e)}
                                            style={{ ...cellInputStyle, ...{ textAlign: 'center', fontWeight: 'bold' } }}
                                        />
                                    </td>
                                    {/* Rate */}
                                    <td style={tdStyle}>
                                        <input value={it.rate} readOnly style={cellInputStyle} />
                                    </td>
                                    {/* Amount */}
                                    <td style={{ ...tdStyle, textAlign: 'right', paddingRight: '12px', fontWeight: 'bold' }}>
                                        {(parseFloat(it.amount) || 0).toFixed(2)}
                                    </td>
                                    {/* Remove button (Clean red cross matching screenshot) */}
                                    <td style={{ ...tdStyle, textAlign: 'center', borderRight: 'none' }}>
                                        <span
                                            onClick={() => removeItem(idx)}
                                            style={{ color: '#ef4444', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer', display: 'inline-block', lineHeight: 1 }}
                                        >
                                            ×
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                        {/* Totals Row (Exactly styled as in the second screenshot) */}
                        <tr style={{ background: '#f2f4f7', fontWeight: 'bold', borderTop: '2px solid #cbd5e1' }}>
                            <td colSpan={6} style={{ padding: '6px 8px', fontSize: '13px', borderRight: '1px solid #cbd5e1', borderBottom: 'none' }}>
                                Total Entry: {items.length}
                            </td>
                            <td style={{ padding: '6px 4px', textAlign: 'center', fontSize: '13px', borderRight: '1px solid #cbd5e1', borderBottom: 'none' }}>
                                {items.reduce((s, it) => s + (parseFloat(it.qty) || 0), 0).toFixed(3)}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: '13px', borderRight: '1px solid #cbd5e1', borderBottom: 'none' }}>
                                Sub Total
                            </td>
                            <td style={{ padding: '6px 12px', textAlign: 'right', fontSize: '14px', fontWeight: 'bold', borderRight: 'none', borderBottom: 'none' }}>
                                {subTotal.toFixed(2)}
                            </td>
                            <td style={{ borderBottom: 'none', borderRight: 'none' }}></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* ── Footer Footer Layout ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: '32px', alignItems: 'start' }}>
                
                {/* Footer Left: Note, Narration, Change Delivery Address */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    <div>
                        <h4 style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: '700', color: '#475569' }}>Note / Payment Terms</h4>
                        <textarea
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            rows={3}
                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', color: '#1e293b', lineHeight: '1.5', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                        />
                    </div>

                    <div>
                        <h4 style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: '700', color: '#475569' }}>Narration / Terms & Conditions</h4>
                        <textarea
                            value={narration}
                            onChange={e => setNarration(e.target.value)}
                            rows={4}
                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', color: '#1e293b', lineHeight: '1.5', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                        />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '2px' }}>
                        <input
                            type="checkbox"
                            id="changeDelivery"
                            checked={changeDeliveryAddress}
                            onChange={e => setChangeDeliveryAddress(e.target.checked)}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <label htmlFor="changeDelivery" style={{ fontSize: '13px', fontWeight: '600', color: '#475569', cursor: 'pointer' }}>Change Delivery Address</label>
                    </div>
                </div>

                {/* Footer Right: Financial Summary */}
                <div style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                    
                    {/* Discount (%) */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Discount</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', overflow: 'hidden', height: '30px' }}>
                                <input
                                    ref={discountRef}
                                    type="number"
                                    value={discountPct}
                                    onChange={e => setDiscountPct(e.target.value)}
                                    onKeyDown={handleDiscountKeyDown}
                                    style={{ width: '50px', padding: '0 6px', border: 'none', textAlign: 'right', fontSize: '13px', outline: 'none', fontWeight: '600' }}
                                />
                            </div>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#ef4444', minWidth: '70px', textAlign: 'right' }}>-{discountAmt.toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Insurance (%) */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Insurance (%)</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', overflow: 'hidden', height: '30px' }}>
                                <input
                                    ref={insuranceRef}
                                    type="number"
                                    value={insurancePct}
                                    onChange={e => setInsurancePct(e.target.value)}
                                    onKeyDown={handleInsuranceKeyDown}
                                    style={{ width: '50px', padding: '0 6px', border: 'none', textAlign: 'right', fontSize: '13px', outline: 'none', fontWeight: '600' }}
                                />
                                <span style={{ padding: '0 8px', background: '#f1f5f9', color: '#475569', fontSize: '12px', fontWeight: '700', borderLeft: '1px solid #cbd5e1', lineHeight: '30px' }}>%</span>
                            </div>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a', minWidth: '70px', textAlign: 'right' }}>{insAmt.toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Extra Charges */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Extra Charges</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                ref={extraChargesRef}
                                type="number"
                                value={extraChargesInput}
                                onChange={e => setExtraChargesInput(e.target.value)}
                                style={{ width: '78px', padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: '6px', textAlign: 'right', fontSize: '13px', height: '30px', boxSizing: 'border-box', fontWeight: '600' }}
                            />
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a', minWidth: '70px', textAlign: 'right' }}>{extrAmt.toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Taxable Amount */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', marginTop: '10px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>Taxable Amount</span>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{taxableAmount.toFixed(2)}</span>
                    </div>

                    {/* Central Tax */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                        <span style={{ fontSize: '13px', color: '#475569' }}>Central Tax ({tax1}%)</span>
                        <span style={{ fontSize: '13px', fontWeight: '500', color: '#0f172a' }}>{centralTax.toFixed(2)}</span>
                    </div>

                    {/* State/UT Tax */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                        <span style={{ fontSize: '13px', color: '#475569' }}>State/UT Tax ({tax2}%)</span>
                        <span style={{ fontSize: '13px', fontWeight: '500', color: '#0f172a' }}>{stateTax.toFixed(2)}</span>
                    </div>

                    {/* Bill Amount */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid #0f172a', paddingTop: '10px', marginTop: '10px' }}>
                        <span style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>Bill Amount</span>
                        <span style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>{billAmount.toFixed(2)}</span>
                    </div>

                </div>

            </div>

            {/* ── Submit / Cancel Button Row ── */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '32px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    style={{ padding: '12px 42px', background: '#1e293b', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                >
                    {submitting ? 'Saving...' : 'OK / Save Return'}
                </button>
                {onClose && (
                    <button
                        onClick={onClose}
                        style={{ padding: '12px 30px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', fontWeight: '600', color: '#475569', cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                        Cancel
                    </button>
                )}
            </div>

        </div>
    );
}

export default AddSalesReturnForm;
